import { useMemo, useRef, useState } from 'react'
import { useAppDispatch } from 'state'
import { updateActiveTournament, updateJoinTournament } from 'state/tournament/actions'
import { useActiveTournamentChanged, useTournamentList } from 'state/tournament/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import Swiper from 'swiper'
import Box from 'UIKit/Box/Box'
import Button from 'UIKit/Button'
import Carousel from 'UIKit/Carousel'
import { StyledPaginationContainer } from 'UIKit/Carousel/Carousel'
import TournamentCard from './TournamentCard'

const TournamentSlides = () => {
  const controlledRef = useRef<Swiper>(null)
  const dispatch = useAppDispatch()
  const { availableTournaments } = useTournamentList()

  const [initialSlide, setInitialSlide] = useState(-1)

  useActiveTournamentChanged((activeId: string) => {
    if (initialSlide === -1) setInitialSlide(availableTournaments.findIndex((tournament) => tournament.id === activeId))
  })

  const slides = useMemo(
    () =>
      availableTournaments?.map((item) => ({
        key: item.id,
        content: (
          <TournamentCard
            tournamentBase={item}
            key={item.id}
            updateJoinTournament={(id) => {
              dispatch(updateJoinTournament({ data: { tournamentId: id, isJoined: true } }))
            }}
          />
        ),
      })),
    [availableTournaments],
  )

  const handleSlideChange = (changedSlide: number) => {
    const tournamentId = availableTournaments[changedSlide].id
    dispatch(updateActiveTournament({ data: { tournamentId } }))
  }

  const isDisbledChangeSlide = availableTournaments.length <= 1

  return (
    <StyledTournamentSlides>
      <StyledCarousel
        pr="20px"
        autoplay={false}
        slides={slides}
        onSwiper={(swiper) => {
          controlledRef.current = swiper
        }}
        loop
        onActiveSlideChange={handleSlideChange}
        key={initialSlide}
        initialSlide={initialSlide}
        pagination
      />

      <StyledArrowLeft
        $isDisbledChangeSlide={isDisbledChangeSlide}
        onClick={
          !isDisbledChangeSlide
            ? () => {
                controlledRef.current.slidePrev()
              }
            : () => {}
        }
      >
        <Icons.ChevronLeftIcon width="14px" height="14px" fill="#5b5c60" />
      </StyledArrowLeft>

      <StyledArrowRight
        $isDisbledChangeSlide={isDisbledChangeSlide}
        onClick={
          !isDisbledChangeSlide
            ? () => {
                controlledRef.current.slideNext()
              }
            : () => {}
        }
      >
        <Icons.ChevronRightIcon width="14px" height="14px" fill="#5b5c60" />
      </StyledArrowRight>
    </StyledTournamentSlides>
  )
}

const StyledTournamentSlides = styled(Box)`
  background: linear-gradient(165deg, rgba(173, 173, 173, 0.08) 7.61%, rgba(173, 173, 173, 0) 112.59%);
  backdrop-filter: blur(4.449999809265137px);
  border-radius: 12px;
  border: 1px solid #353c47;
  position: relative;
  margin-bottom: 12px;
  height: 198px;
`

const StyledArrowBtn = styled(Button)`
  position: absolute;
  top: 0px;
  height: 100%;
  width: 20px;
  padding: 0px;
  border-radius: 0px;
  background: unset !important;
  z-index: 1;

  &:hover {
    background: #34373c !important;
    transition: none !important;
    transform: none !important;
  }
`

const StyledArrowLeft = styled(StyledArrowBtn)<{ $isDisbledChangeSlide }>`
  left: -1px;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`

const StyledArrowRight = styled(StyledArrowBtn)<{ $isDisbledChangeSlide }>`
  right: -1px;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
`

const StyledCarousel = styled(Carousel)`
  height: 100%;
  ${StyledPaginationContainer} {
    display: flex;
    width: fit-content;
    position: absolute;
    bottom: -22px;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;

    .item-pagination-active {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`

export default TournamentSlides
